import { equipmentRegistry } from "../types/equipmentRegistry";

export type ForecastDay = {
  day: string;
  loadScore: number;
  readinessScore: number;
  focus: string;
};

export type TodayScheduleItem = {
  id: string;
  time: string;
  type: "ride" | "strength" | "nutrition" | "recovery" | "race" | "brick";
  title: string;
  details: string[];
  estimatedDuration?: string;
  primaryObjective?: string;
};

export const athleteCommandCenterData = {
  morningBrief: {
    dateLabel: "Friday, July 31",
    readiness: 82,
    hrvMs: 74,
    restingHr: 46,
    sleepHours: 7.9,
    briefing: [
      "Recovery looks excellent today. Your aerobic endurance session aligns well with today's weather if you start before 8:00 AM.",
      "Ride the Canyon Grail because today's mixed-terrain route benefits from wider tires and stable handling."
    ]
  },
  quickActions: {
    recentRideLabel: "Yesterday: 2h 18m mixed-terrain endurance",
    recentRideSummary: "NP 242W | IF 0.77 | 1,806 kJ",
    primaryUploadLabel: "Upload Activity"
  },
  mission: {
    workout: "Aerobic Strength Builder",
    estimatedDuration: "2h 05m",
    estimatedTss: 142,
    expectedCalories: 1780,
    confidence: 0.89,
    blocks: [
      "Warm-up: 20m progressive to 70% FTP",
      "Main Set: 3 x 16m @ 88-92% FTP, 6m easy between",
      "Finish: 20m endurance spin + 4 x 8s seated neuromuscular sprints"
    ]
  },
  todaySchedule: [
    {
      id: "ride-aerobic-strength",
      time: "06:30",
      type: "ride",
      title: "Aerobic Strength Builder",
      details: ["2h 05m"]
    },
    {
      id: "nutrition-recovery",
      time: "12:30",
      type: "nutrition",
      title: "Recovery Nutrition",
      details: ["38g protein", "118g carbohydrate"]
    },
    {
      id: "strength-workout-b",
      time: "17:30",
      type: "strength",
      title: "Full Body Strength",
      details: ["Workout B", "Estimated 75 min"],
      estimatedDuration: "75 min",
      primaryObjective: "Build total-body strength while preserving quality for the next endurance session."
    },
    {
      id: "recovery-evening",
      time: "20:45",
      type: "recovery",
      title: "Recovery",
      details: ["Hot tub", "Mobility", "Sleep target"]
    }
  ] as TodayScheduleItem[],
  weather: {
    rideConditions: "Excellent",
    windImpact: "Moderate crosswind on exposed gravel sectors after 9:30 AM.",
    heatImpact: "Core temperature drift risk increases after 10:45 AM.",
    provider: "AccuWeather (future API)",
    condition: "Partly Cloudy",
    highF: 86,
    lowF: 67,
    windMph: 15,
    gustMph: 22,
    humidity: 64,
    sunrise: "6:13 AM",
    recommendation: "Start before 8:00 AM to avoid heat load drift."
  },
  recovery: {
    eveningProtocol: {
      modality: "Hot tub",
      recommendation: "10-12 minutes, finish at least 75 minutes before sleep"
    },
    mobility: "15 minutes hips, calves, and thoracic spine",
    futureRecommendations: ["Contrast Therapy"],
    confidence: 0.86
  },
  equipmentRecommendation: {
    primary: equipmentRegistry.bikes[1],
    supporting: [equipmentRegistry.technology[0], equipmentRegistry.technology[2], equipmentRegistry.technology[3]],
    reason: "Route includes mixed gravel sectors and sustained tempo segments where thermal pacing and ventilatory trend checks matter.",
    suggestedTirePressure: "42 psi front / 45 psi rear",
    maintenanceStatus: "Green: drivetrain clean, brake rotor noise not detected.",
    rideReadiness: "Ready",
    equipmentStatus: [
      "Garmin Edge 1040 synced and charged",
      "CORE paired and calibrated",
      "Tymewear battery above 80%"
    ],
    futureSupport: ["Chain wax", "Di2 battery", "Tire sealant", "Brake wear"]
  },
  alerts: [
    {
      id: "alert-heat",
      severity: "high" as const,
      category: "Weather",
      title: "Heat Load Warning",
      message: "Projected heat index crosses 94F after 11:00 AM. Move start time earlier or cap final interval.",
      action: "Start between 6:20 AM and 7:30 AM."
    },
    {
      id: "alert-fueling",
      severity: "medium" as const,
      category: "Nutrition",
      title: "Fueling Compliance",
      message: "Last two long rides undershot carbohydrate by about 18g per hour.",
      action: "Pre-mix bottle one and two before departure."
    },
    {
      id: "alert-equipment",
      severity: "low" as const,
      category: "Equipment",
      title: "Rear Tire Sealant Window",
      message: "Sealant is approaching service window for the weekend gravel block.",
      action: "Top up sealant this evening after recovery protocol."
    }
  ],
  train: {
    recentActivities: [
      "Thu: 2h 18m Endurance + Tempo Build",
      "Wed: 1h 12m VO2 Session",
      "Tue: 50m Recovery Spin"
    ],
    workoutLibrary: [
      "Aerobic Strength Builder",
      "Over-Under Threshold 3x12",
      "Race Week Priming Openers"
    ],
    performanceAnalysis: [
      "Power-duration trend",
      "Durability after 1500 kJ",
      "Pw:HR decoupling watch"
    ]
  },
  forecast: [
    { day: "Sat", loadScore: 64, readinessScore: 80, focus: "Endurance volume" },
    { day: "Sun", loadScore: 52, readinessScore: 84, focus: "Recovery spin" },
    { day: "Mon", loadScore: 77, readinessScore: 73, focus: "Threshold repeats" },
    { day: "Tue", loadScore: 58, readinessScore: 81, focus: "Strength + easy ride" },
    { day: "Wed", loadScore: 83, readinessScore: 69, focus: "VO2 development" },
    { day: "Thu", loadScore: 49, readinessScore: 86, focus: "Full recovery" },
    { day: "Fri", loadScore: 71, readinessScore: 78, focus: "Tempo durability" }
  ] as ForecastDay[]
};
